"use client";

import { primaryColor } from "@/lib/theme";
import { Check, ChevronLeft } from "lucide-react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import React from "react";

import PackageIcon from "@/app/assets/svgs/embarca-log/step-box.svg";
import CalcIcon from "@/app/assets/svgs/embarca-log/step-calc.svg";
import PaymentIcon from "@/app/assets/svgs/embarca-log/step-pay.svg";
import SendIcon from "@/app/assets/svgs/embarca-log/step-send.svg";

type Step = {
  id: number;
  label: string;
  href: string;
  icon: string;
};

const STEPS: Step[] = [
  { id: 1, label: "Pacotes", href: "/encomendas", icon: PackageIcon },
  { id: 2, label: "Resumo", href: "/encomendas/pacotes", icon: CalcIcon },
  {
    id: 3,
    label: "Envio",
    href: "/encomendas/dados/remetente",
    icon: SendIcon,
  },
  {
    id: 4,
    label: "Pagamento",
    href: "/encomendas/pagamento",
    icon: PaymentIcon,
  },
];

function resolveCurrentStep(pathname: string | null): number {
  if (!pathname) return 1;
  const p = pathname.toLowerCase();
  if (p.includes("/encomendas/pagamento")) return 4;
  if (p.includes("/encomendas/dados/destinatario")) return 3;
  if (p.includes("/encomendas/dados/remetente")) return 3;
  if (p.includes("/encomendas/pacotes")) return 2;
  if (p.includes("/encomendas/envio")) return 2;
  if (p.includes("/encomendas")) return 1;
  return 1;
}

export interface EncomendasStepsProps {
  hideUserOptions?: boolean;
}

export default function EncomendasSteps({
  hideUserOptions: _hideUserOptions = false,
}: EncomendasStepsProps) {
  const pathname = usePathname();
  const router = useRouter();
  const current = resolveCurrentStep(pathname);

  const isTrackingPage = (pathname || "")
    .toLowerCase()
    .includes("/encomendas/rastreio");
  if (isTrackingPage) return null;

  return (
    <div className="w-full mb-6 md:mb-5">
      <div className="flex items-center justify-between gap-4">
        {/* Left: back + stepper */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Back button */}
          <button
            id="btn-steps-back"
            type="button"
            onClick={() => router.back()}
            disabled={current === 1}
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl border-2 border-line bg-white text-slate hover:border-ink hover:text-ink transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-line disabled:hover:text-slate"
            aria-label="Voltar"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
          </button>

          {/* Step pills — desktop */}
          <div className="hidden md:flex items-center gap-1.5 min-w-0 overflow-hidden">
            {STEPS.map((step, idx) => {
              const done = step.id < current;
              const active = step.id === current;
              const future = step.id > current;

              return (
                <React.Fragment key={step.id}>
                  {idx > 0 && (
                    <div
                      className="shrink-0 h-px w-4 transition-colors"
                      style={{
                        backgroundColor:
                          done || active ? primaryColor : "#E2E5E9",
                      }}
                    />
                  )}
                  <div
                    className="shrink-0 flex items-center gap-1.5 rounded-xl border-2 transition-colors px-3 py-1.5"
                    style={{
                      borderColor: active || done ? primaryColor : "#E2E5E9",
                      backgroundColor: done ? "#E8EAED" : "#FFFFFF",
                    }}
                  >
                    <div
                      className="flex items-center justify-center rounded-lg transition-colors"
                      style={{
                        width: active ? 24 : 20,
                        height: active ? 24 : 20,
                        backgroundColor: active ? primaryColor : "transparent",
                      }}
                    >
                      {done ? (
                        <Check
                          className="w-3.5 h-3.5"
                          strokeWidth={3}
                          style={{ color: primaryColor }}
                        />
                      ) : (
                        <Image
                          src={step.icon}
                          alt={step.label}
                          width={active ? 14 : 12}
                          height={active ? 14 : 12}
                          className={`${active ? "brightness-0 invert" : future ? "opacity-30" : ""}`}
                        />
                      )}
                    </div>
                    {active && (
                      <span className="text-fine font-bold text-ink whitespace-nowrap">
                        {step.label}
                      </span>
                    )}
                    {future && (
                      <span className="text-micro font-semibold text-mist whitespace-nowrap">
                        {step.label}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Step indicator — mobile */}
          <div className="flex md:hidden items-center gap-2 min-w-0">
            {/* Progress dots */}
            <div className="flex items-center gap-1">
              {STEPS.map((step) => {
                const done = step.id < current;
                const active = step.id === current;
                return (
                  <div
                    key={step.id}
                    className="rounded-full transition-[width,background-color] duration-200"
                    style={{
                      width: active ? 20 : 6,
                      height: 6,
                      backgroundColor:
                        done || active ? primaryColor : "#E2E5E9",
                    }}
                  />
                );
              })}
            </div>
            {/* Active step label */}
            <div
              className="flex items-center gap-1.5 rounded-xl border-2 bg-white px-3 py-1.5 shrink-0"
              style={{ borderColor: primaryColor }}
            >
              <div
                className="w-6 h-6 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                <Image
                  src={STEPS[current - 1].icon}
                  alt={STEPS[current - 1].label}
                  width={14}
                  height={14}
                  className="brightness-0 invert"
                />
              </div>
              <span className="text-fine font-bold text-ink whitespace-nowrap">
                {STEPS[current - 1].label}
              </span>
              <span className="text-micro text-mist whitespace-nowrap">
                {current}/{STEPS.length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
